import dayjs from 'dayjs';
import { HISTORY_FILTER_FIELDS } from '../constants/historyData';
import {
  formatDecimal,
  scaledIntToNormal,
} from '../utils/decimalValues';

const USD_DECIMAL_PLACES = 2;

export const HISTORY_FILTER_FIELD_KEYS = HISTORY_FILTER_FIELDS;

function unwrapFilterPayload(response) {
  if (Array.isArray(response?.data)) {
    return response;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data;
  }

  return response && typeof response === 'object' ? response : {};
}

function normalizeDate(value) {
  if (!value) return null;

  const date = dayjs(value);

  return date.isValid() ? date.format('YYYY-MM-DD') : null;
}

function normalizeUsdStat(value) {
  const raw = /^-?\d+(?:[.,]\d+)?$/.test(String(value ?? '').trim())
    ? String(value).trim()
    : '0';
  const normalized = scaledIntToNormal(raw, USD_DECIMAL_PLACES, {
    fixed: false,
    trimZeros: true,
  });

  return {
    raw,
    value: normalized,
    formatted: formatDecimal(normalized, USD_DECIMAL_PLACES, {
      prefix: '$',
      minFractionDigits: 2,
    }),
  };
}

function normalizeHistoryFilterCard(card) {
  const id = String(card?._id ?? card?.id ?? '').trim();

  if (!id) return null;

  const last4 = String(card?.last4 ?? card?.lastFour ?? '').trim();
  const productName = String(card?.static_data?.name ?? '').trim();
  const fullName = String(card?.full_name ?? '').trim();
  const fallbackName = String(card?.name ?? card?.label ?? '').trim();
  const label = productName || fullName || fallbackName;

  return {
    id,
    _id: id,
    label,
    name: label,
    productName,
    fullName,
    last4,
    lastFour: last4,
    style: String(card?.static_data?.style ?? card?.style ?? '').trim(),
    status: card?.status ?? '',
    type: card?.type ?? '',
  };
}

export function mergeHistoryFilterCards(previousCards = [], nextCards = []) {
  const nextById = new Map(nextCards.map((card) => [card.id, card]));
  const merged = previousCards.map((card) => {
    const updatedCard = nextById.get(card.id);

    if (!updatedCard) return card;

    nextById.delete(card.id);
    return { ...card, ...updatedCard };
  });

  return [...merged, ...nextById.values()];
}

export function normalizeHistoryFilterMetadata(response) {
  const payload = unwrapFilterPayload(response);
  const filters = Array.isArray(payload.data) ? payload.data : [];
  const cardFilter = filters.find((filter) => (
    filter?.field === HISTORY_FILTER_FIELD_KEYS.CARDS
    || filter?.type === 'card_enum'
  ));
  const dateFilter = filters.find((filter) => (
    filter?.field === HISTORY_FILTER_FIELD_KEYS.DATES
    || filter?.type === 'date_range'
  ));
  const typeFilter = filters.find((filter) => (
    filter?.field === HISTORY_FILTER_FIELD_KEYS.TYPE
    && filter?.type === 'enum'
  ));
  const cards = (Array.isArray(cardFilter?.values) ? cardFilter.values : [])
    .map(normalizeHistoryFilterCard)
    .filter(Boolean);
  const minDate = normalizeDate(dateFilter?.values?.min);
  const maxDate = normalizeDate(dateFilter?.values?.max);
  const stats = payload.stats_for_range || {};

  return {
    cards,
    minDate,
    maxDate,
    dateRange: {
      min: minDate,
      max: maxDate,
    },
    typeValues: Array.isArray(typeFilter?.values) ? [...typeFilter.values] : [],
    stats: {
      income: normalizeUsdStat(stats.income_value_usd),
      outcome: normalizeUsdStat(stats.outcome_value_usd),
    },
    isMore: Boolean(payload.is_more),
  };
}
